/**
 * Phase 7-lite — Cross-platform PWA install pill.
 * Replaces the old IOSInstallBanner with platform-aware behaviour:
 *
 *   • iOS Safari (not standalone)   → tap opens the 3-slide walkthrough
 *     modal (real iOS share-button instructions per layout variant)
 *   • Android Chrome / Desktop      → tap fires `beforeinstallprompt`
 *     native install dialog (1-tap install)
 *   • Already installed (standalone) → component renders nothing
 *
 * Layout: floating pill, 80px above screen bottom (not flush) so it has a
 * physical safe-area gap from any page-level CTA below it. The pill itself
 * has a 48px-tall tap zone (iOS HIG minimum) so near-miss taps still
 * register as "open walkthrough" rather than passing through to underlying
 * Register / Login buttons.
 *
 * Dismissed for 14 days (localStorage). The persistent header pill
 * (`PWAInstallHeaderPill`) remains as an escape hatch on every page.
 */
import { useEffect, useState } from 'react';
import { X, Download } from 'lucide-react';
import { useFlag } from '../context/FeatureFlagsContext';
import { useTheme } from '../context/ThemeContext';
import { detectPlatform, canPromptInstall, promptInstall, isPreviewInstallMode } from '../utils/installPwa';
import { PWAInstallWalkthrough } from './PWAInstallWalkthrough';

const DISMISS_KEY = 'pwa_install_pill_dismissed_v2';
const DISMISS_DAYS = 14;

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch (_) { return false; }
}

export function PWAInstallPill() {
  const enabled = useFlag('customer.pwa_install_banner', true);
  const { isModern } = useTheme();
  const [platform, setPlatform] = useState(null);
  const [visible, setVisible] = useState(false);
  const [walkthrough, setWalkthrough] = useState(false);
  const [bipReady, setBipReady] = useState(false);

  // V2 dashboard removed Feb 2026; no route suppression needed anymore.
  const suppressedRoute = false;

  // Reserve scroll room so the floating pill never hides page-bottom CTAs.
  useEffect(() => {
    const on = visible && !!platform;
    document.body.classList.toggle('am-has-install-pill', on);
    return () => document.body.classList.remove('am-has-install-pill');
  }, [visible, platform]);

  useEffect(() => {
    if (typeof window === 'undefined' || !enabled) return undefined;
    const p = detectPlatform();
    setPlatform(p);
    setBipReady(canPromptInstall());

    // Preview-install mode bypasses standalone + dismiss checks so the
    // operator can demo the flow inside the installed PWA.
    const preview = isPreviewInstallMode();

    if (!preview && (p.isStandalone || wasRecentlyDismissed())) return undefined;
    if (!p.isIOS && !p.isAndroid && !p.isDesktop) return undefined;

    const t = setTimeout(() => setVisible(true), 1500);
    const onBip = () => setBipReady(true);
    const onInstalled = () => setVisible(false);
    window.addEventListener('pwa-install-available', onBip);
    window.addEventListener('pwa-installed', onInstalled);
    return () => {
      clearTimeout(t);
      window.removeEventListener('pwa-install-available', onBip);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, [enabled]);

  const dismiss = (e) => {
    e?.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setVisible(false);
  };

  const handleInstall = async () => {
    if (!platform) return;
    if (platform.isIOS) {
      setWalkthrough(true);
      return;
    }
    if (bipReady) {
      const choice = await promptInstall();
      if (choice?.outcome === 'accepted') setVisible(false);
    } else {
      // Android Chrome eligibility not yet met — fall back to walkthrough
      setWalkthrough(true);
    }
  };

  if (!visible || !platform || suppressedRoute) {
    return (
      <PWAInstallWalkthrough open={walkthrough} onClose={() => setWalkthrough(false)} />
    );
  }

  // 80px clear-zone above the bottom of the screen so we don't sit on top of
  // page-level CTAs. The pill is centered horizontally with a fixed max-width.
  return (
    <>
      <div
        className="pwa-install-pill fixed left-1/2 -translate-x-1/2 z-[60] pointer-events-none"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)' }}
        data-testid="pwa-install-pill"
      >
        <button
          onClick={handleInstall}
          className={isModern
            ? 'pointer-events-auto am-cta flex items-center gap-2 px-4 py-3 min-h-[48px]'
            : 'pointer-events-auto flex items-center gap-2 bg-[#0C0612] border border-[#39FF14] px-4 py-3 rounded-full shadow-[0_4px_30px_rgba(57,255,20,0.4)] hover:bg-[#1A0A2A] hover:shadow-[0_4px_40px_rgba(57,255,20,0.6)] transition-all min-h-[48px]'}
          style={isModern ? { fontFamily: 'var(--am-font-sans)' } : undefined}
          data-testid="pwa-install-pill-cta"
          aria-label="Install Ash & Merch app"
        >
          <Download className={`w-4 h-4 shrink-0 ${isModern ? 'text-white' : 'text-[#39FF14]'}`} />
          <span className={isModern ? 'text-white text-sm font-semibold' : 'font-mono text-[#39FF14] text-xs font-bold tracking-wider'}>
            {isModern ? 'Install the app' : 'INSTALL APP'}
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={dismiss}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') dismiss(e); }}
            className={`ml-1 w-7 h-7 -my-2 -mr-2 flex items-center justify-center rounded-full ${isModern ? 'text-white/80 hover:text-white hover:bg-white/15' : 'text-[#6B8E73] hover:text-[#FF3B30]'}`}
            aria-label="Dismiss for 14 days"
            data-testid="pwa-install-pill-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </span>
        </button>
      </div>
      <PWAInstallWalkthrough open={walkthrough} onClose={() => setWalkthrough(false)} />
    </>
  );
}

export default PWAInstallPill;
